// @flow

/**
 * Module dependencies.
 */

import { Col, Container, Row, Section } from 'client/components/core/grid';
import { breakpoints, colors, columns, media, units } from 'client/styles';
import { position } from 'polished';
import BackgroundImage from 'client/components/core/images/background-image';
import FormTransferRequest from 'client/components/forms/form-transfer-request';
import Layout from 'client/components/core/layout';
import Logo from 'client/components/core/logo';
import MediaQuery from 'react-responsive';
import Overlay from 'client/components/header/overlay';
import React from 'react';
import Text from 'client/components/core/type';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  formTransferRequestSubject: string,
  lead: string,
  title: string
};

/**
 * `StyledSection` component.
 */

const StyledSection = styled(Section)`
  align-items: center;
  color: ${colors.white};
  display: flex;
  padding: ${units(10)} 0 ${units(5)};
  width: 100%;

  &:after {
    ${position('absolute', '0', '0', '0', null)};

    background-color: ${colors.shadesOfBlue.bigStoneDarker};
    content: '';
    display: none;
    left: auto;
    pointer-events: none;
    width: ${columns(3)}%;
    z-index: 2;
  }

  ${media.min.sm`
    min-height: 680px;
  `}

  ${media.min.lg`
    padding: ${units(10)} 0 ${units(4)};

    &:after {
      display: block;
    }
  `}
`;

/**
 * `ContentWrapper` component.
 */

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 3;

  ${media.max.lg`
    margin: ${units(8)} 0 ${units(2)};
  `}
`;

/**
 * `VerticalAlignment` component.
 */

const VerticalAlignment = styled.div`
  ${media.min.lg`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
  `}
`;

/**
 * `StyledCol` component.
 */

const StyledCol = styled(Col)`
  ${media.min.lg`
    align-self: flex-start;
    margin-bottom: ${units(8)};
    margin-top: ${units(8)};
    padding-bottom: 119px;
  `}
`;

/**
 * `FormContainer` component.
 */

const FormContainer = styled.div`
  align-items: center;
  display: flex;
  margin-top: ${units(10)};
  min-height: 533px;

  ${media.min.sm`
    min-height: 483px;
  `}

  ${media.min.lg`
    display: block;
    margin-top: ${units(2.5)};
    min-height: initial;
  `}
`;

/**
 * `Header` component.
 */

function Header(props: Props): React$Element<*> {
  const { formTransferRequestSubject, lead, title } = props;

  return (
    <StyledSection bgColor={colors.brandSecondary}>
      <BackgroundImage filename={'lead-header'} />

      <Overlay />

      <ContentWrapper>
        <Container>
          <Row>
            <VerticalAlignment>
              <StyledCol
                lg={5}
                lgOffset={1}
                md={8}
                xs={10}
                xsOffset={1}
              >
                <MediaQuery minWidth={breakpoints.md}>
                  <Layout marginBottom={units(10)}>
                    <Logo />
                  </Layout>
                </MediaQuery>

                <Text.H1
                  color={colors.brandPrimary}
                  marginBottom={units(0)}
                >
                  {title}
                </Text.H1>

                <Text.H4>
                  {lead}
                </Text.H4>
              </StyledCol>

              <Col
                lg={4}
                lgOffset={1}
                md={8}
                mdOffset={2}
                xs={10}
                xsOffset={1}
              >
                <FormContainer>
                  <FormTransferRequest
                    autofocus
                    fluid
                    subject={formTransferRequestSubject}
                  />
                </FormContainer>
              </Col>
            </VerticalAlignment>
          </Row>
        </Container>
      </ContentWrapper>
    </StyledSection>
  );
}

/**
 * Export `Header`.
 */

export default Header;



// WEBPACK FOOTER //
// ../src/client/components/home/header.js